import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Video, ArrowLeft, X, Plus, Package, Wrench, MapPin, Clock, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import api from '@/lib/api';
import { toast } from 'sonner';

const conditions = [
  { value:'nuevo', label:'Nuevo' }, { value:'como_nuevo', label:'Como nuevo' },
  { value:'bueno', label:'Buen estado' }, { value:'usado', label:'Usado' },
];
const locationTypes = [
  { value:'store', label:'Local Establecido', icon: MapPin },
  { value:'home', label:'A Domicilio', icon: Package },
  { value:'digital', label:'Digital / Remoto', icon: Wrench },
];
const locationOptions = ['Ocoyoacac'];
const neighborhoodOptions = [
  'Santa Maria', 'San Antonio', 'San Miguel', 'Coapanoaya', 'Tepexoyuca',
  'Acazulco', 'Cholula', 'Santiaguito', 'El Pedregal', 'El Pirame', 'Benevento',
];

export default function UploadPage() {
  const navigate = useNavigate();
  const imgRef = useRef(null);
  const vidRef = useRef(null);
  const [itemType, setItemType] = useState('product');
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [title, setTitle] = useState('');
  const [price, setPrice] = useState('');
  const [description, setDescription] = useState('');
  const [condition, setCondition] = useState('bueno');
  const [locationType, setLocationType] = useState('');
  const [serviceAddress, setServiceAddress] = useState('');
  const [serviceHours, setServiceHours] = useState('');
  const [location, setLocation] = useState('');
  const [neighborhood, setNeighborhood] = useState('');
  const [uploading, setUploading] = useState(false);

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length + imageFiles.length > 5) { toast.error('Maximo 5 imagenes'); return; }
    setImageFiles(prev => [...prev, ...files]);
    setImagePreviews(prev => [...prev, ...files.map(f => URL.createObjectURL(f))]);
  };
  const removeImage = (idx) => {
    setImageFiles(prev => prev.filter((_, i) => i !== idx));
    setImagePreviews(prev => prev.filter((_, i) => i !== idx));
  };
  const handleVideoSelect = (e) => { const f = e.target.files?.[0]; if (!f) return; setVideoFile(f); setVideoPreview(URL.createObjectURL(f)); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title || !price) { toast.error('Titulo y precio obligatorios'); return; }
    if (itemType === 'service' && !locationType) { toast.error('Selecciona tipo de ubicacion'); return; }
    if (itemType === 'service' && locationType === 'store' && !serviceAddress) { toast.error('Direccion del local requerida'); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append('title', title);
    fd.append('price', parseFloat(price));
    fd.append('description', description);
    fd.append('item_type', itemType);
    fd.append('condition', itemType === 'product' ? condition : '');
    fd.append('service_location_type', itemType === 'service' ? locationType : '');
    fd.append('service_address', itemType === 'service' ? serviceAddress : '');
    fd.append('service_hours', itemType === 'service' ? serviceHours : '');
    fd.append('location', location);
    fd.append('neighborhood', neighborhood);
    if (videoFile) fd.append('video', videoFile);
    imageFiles.forEach(f => fd.append('images', f));
    try {
      await api.post('/items', fd);
      const msg = itemType === 'wanted' ? 'Busqueda publicada!' : itemType === 'service' ? 'Servicio publicado!' : 'Articulo publicado!';
      toast.success(msg);
      navigate('/locker');
    } catch { toast.error('Error al subir'); } finally { setUploading(false); }
  };

  const isSvc = itemType === 'service';
  const isWanted = itemType === 'wanted';

  return (
    <div data-testid="upload-page" className="min-h-screen bg-background page-with-nav">
      <div className="max-w-md mx-auto px-4">
        <div className="flex items-center gap-3 mb-6">
          <button data-testid="upload-back-btn" onClick={() => navigate(-1)}><ArrowLeft className="w-6 h-6 text-foreground" /></button>
          <h1 className="font-heading text-2xl text-primary uppercase">PUBLICAR</h1>
        </div>
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Type Selector - 3 options */}
          <div className="space-y-2">
            <label className="font-body font-medium text-foreground text-sm">Que estas publicando?</label>
            <div className="grid grid-cols-3 gap-2">
              <button type="button" data-testid="upload-type-product" onClick={() => setItemType('product')}
                className={`flex items-center justify-center gap-1.5 px-3 py-3 rounded-sm border-2 font-body font-bold text-xs transition-all ${itemType === 'product' ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:border-foreground'}`}>
                <Package className="w-4 h-4" /> Producto
              </button>
              <button type="button" data-testid="upload-type-service" onClick={() => setItemType('service')}
                className={`flex items-center justify-center gap-1.5 px-3 py-3 rounded-sm border-2 font-body font-bold text-xs transition-all ${itemType === 'service' ? 'border-orange-400 bg-orange-400/10 text-orange-400' : 'border-border text-muted-foreground hover:border-foreground'}`}>
                <Wrench className="w-4 h-4" /> Servicio
              </button>
              <button type="button" data-testid="upload-type-wanted" onClick={() => setItemType('wanted')}
                className={`flex items-center justify-center gap-1.5 px-3 py-3 rounded-sm border-2 font-body font-bold text-xs transition-all ${isWanted ? 'border-[#FFD700] bg-[#FFD700]/10 text-[#FFD700]' : 'border-border text-muted-foreground hover:border-foreground'}`}>
                <Search className="w-4 h-4" /> Se Busca
              </button>
            </div>
          </div>

          {/* Images — optional for wanted */}
          {!isWanted && (
            <div className="space-y-2">
              <label className="font-body font-medium text-foreground text-sm">Fotos (max 5)</label>
              <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2">
                {imagePreviews.map((p, i) => (
                  <div key={i} className="w-24 h-24 rounded-sm overflow-hidden bg-zinc-900 border-2 border-border shrink-0 relative">
                    <img src={p} className="object-cover w-full h-full" alt="" />
                    <button type="button" onClick={() => removeImage(i)} className="absolute top-1 right-1 bg-black/70 p-0.5 rounded-sm"><X className="w-3 h-3 text-white" /></button>
                  </div>
                ))}
                {imageFiles.length < 5 && (
                  <button type="button" data-testid="upload-add-image-btn" onClick={() => imgRef.current?.click()}
                    className="w-24 h-24 rounded-sm border-2 border-dashed border-border flex flex-col items-center justify-center gap-1 shrink-0 hover:border-primary transition-colors">
                    <Plus className="w-5 h-5 text-muted-foreground" /><span className="text-muted-foreground text-[10px] font-body">Foto</span>
                  </button>
                )}
              </div>
              <input ref={imgRef} type="file" accept="image/*" multiple onChange={handleImageSelect} className="hidden" />
            </div>
          )}

          {/* Optional images for wanted */}
          {isWanted && (
            <div className="space-y-2">
              <label className="font-body font-medium text-foreground text-sm">Foto de referencia (opcional)</label>
              <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2">
                {imagePreviews.map((p, i) => (
                  <div key={i} className="w-24 h-24 rounded-sm overflow-hidden bg-zinc-900 border-2 border-border shrink-0 relative">
                    <img src={p} className="object-cover w-full h-full" alt="" />
                    <button type="button" onClick={() => removeImage(i)} className="absolute top-1 right-1 bg-black/70 p-0.5 rounded-sm"><X className="w-3 h-3 text-white" /></button>
                  </div>
                ))}
                {imageFiles.length < 5 && (
                  <button type="button" data-testid="upload-add-image-btn" onClick={() => imgRef.current?.click()}
                    className="w-24 h-24 rounded-sm border-2 border-dashed border-[#FFD700]/40 flex flex-col items-center justify-center gap-1 shrink-0 hover:border-[#FFD700] transition-colors">
                    <Plus className="w-5 h-5 text-muted-foreground" /><span className="text-muted-foreground text-[10px] font-body">Foto</span>
                  </button>
                )}
              </div>
              <input ref={imgRef} type="file" accept="image/*" multiple onChange={handleImageSelect} className="hidden" />
            </div>
          )}

          {/* Video — only products/services */}
          {!isWanted && (
            <div className="space-y-2">
              <label className="font-body font-medium text-foreground text-sm">Video (opcional)</label>
              {videoPreview ? (
                <div className="relative aspect-video bg-zinc-900 rounded-sm overflow-hidden border-2 border-border">
                  <video src={videoPreview} className="object-contain w-full h-full" controls muted />
                  <button type="button" onClick={() => { setVideoFile(null); setVideoPreview(null); }} className="absolute top-2 right-2 bg-background/80 p-1.5 rounded-sm"><X className="w-4 h-4 text-foreground" /></button>
                </div>
              ) : (
                <button type="button" data-testid="upload-add-video-btn" onClick={() => vidRef.current?.click()}
                  className="w-full h-16 bg-card border-2 border-dashed border-border rounded-sm flex items-center justify-center gap-3 hover:border-primary transition-colors">
                  <Video className="w-5 h-5 text-muted-foreground" /><span className="text-muted-foreground font-body text-sm">Agregar video</span>
                </button>
              )}
              <input ref={vidRef} type="file" accept="video/*" onChange={handleVideoSelect} className="hidden" />
            </div>
          )}

          {/* Title */}
          <div className="space-y-2">
            <label className="font-body font-medium text-foreground text-sm">{isWanted ? 'Que buscas?' : 'Titulo'}</label>
            <Input data-testid="upload-title-input" value={title} onChange={e => setTitle(e.target.value)}
              placeholder={isWanted ? 'Ej: Busco Bici de Montana' : isSvc ? 'Ej: Reparacion de Celulares' : 'Ej: PS5 Slim 1TB'} className="rounded-sm border-2 bg-card font-body h-12" />
          </div>

          {/* Price */}
          <div className="space-y-2">
            <label className="font-body font-medium text-foreground text-sm">{isWanted ? 'Presupuesto (MXN)' : isSvc ? 'Costo Base (MXN)' : 'Precio (MXN)'}</label>
            <Input data-testid="upload-price-input" type="number" value={price} onChange={e => setPrice(e.target.value)} placeholder={isWanted ? '3000' : isSvc ? '500' : '5000'} className="rounded-sm border-2 bg-card font-body h-12" />
          </div>

          {/* Location — fixed to Ocoyoacac + Colonia picker */}
          <div className="space-y-2">
            <label className="font-body font-medium text-foreground text-sm flex items-center gap-2"><MapPin className="w-4 h-4 text-primary" /> Ubicacion</label>
            <div className="flex flex-wrap gap-2">
              <button type="button" data-testid="upload-location-ocoyoacac"
                onClick={() => { setLocation(location === 'Ocoyoacac' ? '' : 'Ocoyoacac'); setNeighborhood(''); }}
                className={`px-3 py-2 rounded-sm border-2 font-body text-sm transition-colors ${location === 'Ocoyoacac' ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground'}`}>
                Ocoyoacac
              </button>
            </div>
          </div>

          {/* Colonia — shown when Ocoyoacac selected */}
          {location === 'Ocoyoacac' && (
            <div className="space-y-2">
              <label className="font-body font-medium text-foreground text-sm flex items-center gap-2"><MapPin className="w-4 h-4 text-zinc-400" /> Colonia</label>
              <div className="flex flex-wrap gap-2">
                {neighborhoodOptions.map(hood => (
                  <button key={hood} type="button" data-testid={`upload-hood-${hood.toLowerCase().replace(/\s/g,'-')}`}
                    onClick={() => setNeighborhood(neighborhood === hood ? '' : hood)}
                    className={`px-3 py-2 rounded-sm border-2 font-body text-xs transition-colors ${neighborhood===hood ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground'}`}>
                    {hood}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Condition — products only */}
          {!isSvc && !isWanted && (
            <div className="space-y-2">
              <label className="font-body font-medium text-foreground text-sm">Condicion</label>
              <div className="flex flex-wrap gap-2">
                {conditions.map(c => (
                  <button key={c.value} type="button" data-testid={`upload-condition-${c.value}`} onClick={() => setCondition(c.value)}
                    className={`px-3 py-2 rounded-sm border-2 font-body text-sm transition-colors ${condition===c.value ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground'}`}>
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Service fields */}
          {isSvc && (
            <>
              <div className="space-y-2">
                <label className="font-body font-medium text-foreground text-sm">Tipo de Ubicacion</label>
                <div className="flex flex-wrap gap-2">
                  {locationTypes.map(lt => (
                    <button key={lt.value} type="button" data-testid={`upload-loc-${lt.value}`} onClick={() => setLocationType(lt.value)}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-sm border-2 font-body text-sm transition-colors ${locationType===lt.value ? 'border-orange-400 bg-orange-400/10 text-orange-400' : 'border-border text-muted-foreground'}`}>
                      <lt.icon className="w-4 h-4" /> {lt.label}
                    </button>
                  ))}
                </div>
              </div>
              {locationType === 'store' && (
                <div className="space-y-2">
                  <label className="font-body font-medium text-foreground text-sm flex items-center gap-2"><MapPin className="w-4 h-4 text-orange-400" /> Direccion del Local</label>
                  <Input data-testid="upload-address-input" value={serviceAddress} onChange={e => setServiceAddress(e.target.value)}
                    placeholder="Col. Centro #45, CDMX" className="rounded-sm border-2 bg-card font-body h-12" />
                </div>
              )}
              <div className="space-y-2">
                <label className="font-body font-medium text-foreground text-sm flex items-center gap-2"><Clock className="w-4 h-4 text-orange-400" /> Horario de Servicio</label>
                <Input data-testid="upload-hours-input" value={serviceHours} onChange={e => setServiceHours(e.target.value)}
                  placeholder="Lun-Vie 9am-6pm" className="rounded-sm border-2 bg-card font-body h-12" />
              </div>
            </>
          )}

          {/* Description */}
          <div className="space-y-2">
            <label className="font-body font-medium text-foreground text-sm">{isWanted ? 'Describe lo que buscas' : 'Descripcion (opcional)'}</label>
            <textarea data-testid="upload-description-input" value={description} onChange={e => setDescription(e.target.value)}
              placeholder={isWanted ? 'Detalles de lo que necesitas...' : isSvc ? 'Describe tu servicio...' : 'Describe tu articulo...'} rows={3}
              className="w-full rounded-sm border-2 border-border bg-card font-body text-foreground p-3 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-primary" />
          </div>

          <Button data-testid="upload-submit-btn" type="submit" disabled={uploading||!title||!price}
            className={`w-full h-14 rounded-sm font-body font-bold uppercase text-base ${isWanted ? 'bg-[#FFD700] text-black hover:bg-[#FFD700]/90' : 'bg-primary text-primary-foreground hover:bg-primary/90'}`}>
            {uploading ? 'SUBIENDO...' : isWanted ? 'PUBLICAR BUSQUEDA' : isSvc ? 'PUBLICAR SERVICIO' : 'PUBLICAR ARTICULO'}
          </Button>
        </form>
      </div>
    </div>
  );
}
